import { ReactComponent as UserIcon } from "assets/images/user.svg";
import { useStyles } from "./styles";
import { TextField } from "@material-ui/core";
import { CurrentBrand, TeamPricesData } from "modules/Types";
import { useEffect, useState } from "react";
import { Body2, Subtitle1, Subtitle2 } from "components/shareable/Typography";
import { isValidEmail } from "modules/Utils";
import Banner from "elements/Banner";
import BrandsList from "../BrandsList/BrandsList";
import useScreen from "Hooks/useScreen";
import { getSimulationPrices_api } from "api/organizationApi"
import { toast } from "react-toastify"
import { TOAST_ERROR_OPTIONS, TOAST_SUCCESS_OPTIONS } from "../../Organization_constants"
import { TEAM_PLAN_ADDITIONAL_SEAT_PRICE, TEAM_PLAN_INCLUDED_SEATS } from "modules/Const"
import { Container } from "modules/Sty";
import MemberPermissionsSelect from '../MemberPermissionsSelect';
import useOrganization from 'Hooks/useOrganization';
import Colors from "modules/Colors";
import ModalSymphony from "elements/ModalSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import { OrganizationRole, UserPermissionOnBrand } from "modules/Const";
import { toastError } from "modules/Toast";

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddMemberModal = ({
  isOpen,
  onClose
}: AddMemberModalProps) => {
  const classes = useStyles();
  const { teamMembers, addOrganizationTeamMember, getOrganizationBrands, getOrganizationId } = useOrganization();
  const organizationId = getOrganizationId();
  const organizationBrands = getOrganizationBrands();

  const { mobileView } = useScreen();

  const [name, setName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [role, setRole] = useState<OrganizationRole | null>(null)

  // Default to admin permission level for all artists
  const [brandsLocal, setBrandsLocal] = useState<CurrentBrand[]>(
    organizationBrands.map((brand: CurrentBrand) => ({ ...brand, permissionLevel: brand?.permissionLevel || UserPermissionOnBrand.ADMINISTRATOR }))
  )
  const [extraCosts, setExtraCosts] = useState<TeamPricesData | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const isMissingFields = !name || !lastName || !role || !email || (email?.length && !isValidEmail(email)) || Boolean(brandsLocal.find(artist => !artist.permissionLevel))

  useEffect(() => {
    switch (role) {
      case OrganizationRole.ADMINISTRATOR:
        setBrandsLocal(organizationBrands.map((brand: CurrentBrand) => ({ ...brand, permissionLevel: UserPermissionOnBrand.ADMINISTRATOR })));
        break;
      case OrganizationRole.MEMBER:
        setBrandsLocal(organizationBrands.map((brand: CurrentBrand) => ({ ...brand, permissionLevel: UserPermissionOnBrand.VIEW_ONLY })));
        break;
    }
  }, [role])

  useEffect(() => {
    if (teamMembers.length >= TEAM_PLAN_INCLUDED_SEATS) {
      // getExtraCost()
    }
  }, [teamMembers])

  const onAddMember = async () => {
    // NOTE: the full form must be filled out before adding a member
    if (!name || !lastName || !email || !role || !isValidEmail(email)) {
      return
    }

    try {
      setIsLoading(true)
      const newMember = {
        name,
        lastName,
        email,
        role: role
      }

      const addTeamMemberPayload = {
        member: newMember,
        brands: brandsLocal
      }
      await addOrganizationTeamMember(addTeamMemberPayload, organizationId)
      toast.success("New member added to team! Your new teammate will receive an email with the invite.", TOAST_SUCCESS_OPTIONS)
      setName('')
      setLastName('')
      setEmail('')
      setRole(null)
      setBrandsLocal([])
      onClose();
    } catch (error: any) {
      let errorMessage = "Something went wrong adding the member"
      switch (error?.response?.data?.message) {
        case 'User already belongs to an organization':
          errorMessage = "This user already belongs to another Team. Please contact support if you need to add them to this Team."
          break;
        case 'User already invited to your organization':
          errorMessage = "This user has already been invited to your organization. Please contact support if you need help getting them added to the Team."
          break;
      }
      toastError(errorMessage)
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const getExtraCost = async () => {
    const extraCost = await getSimulationPrices_api(organizationId, { typeSimulation: 'add-seat' });
    setExtraCosts(extraCost)
  }

  return (
    <ModalSymphony
      className="addMemberModalComponent"
      isOpen={isOpen}
      fullScreen={mobileView}
      onClose={() => {
        if (isLoading) {
          return
        }
        onClose()
      }}
      title="Add a new teammate"
      subtitle="Manage the users who can access your organization and profiles."
      headerIcon={<UserIcon className={classes.userIcon} />}
      maxWidth={500}
    >
      {/* TODO: in the future we should handle prices based on currency selected */}
      {(teamMembers.length >= TEAM_PLAN_INCLUDED_SEATS && extraCosts) &&
        <Banner className="addMemberModalBanner3" color={Colors.yellowLight} style={{ marginBottom: 16 }}>
          <div className="flex flex-col gap-1">
            <Subtitle1>You're out of seats</Subtitle1>
            <Body2 color={Colors.textColor}>
              You'll be charged with + ${extraCosts.priceSeatRemaining} on your next invoice and then ${extraCosts.priceSeat} per month for this new user
            </Body2>
          </div>
          <div className="flex gap-2 justify-between">
            <Body2>Extra user prorated {extraCosts.remainingDays} days</Body2>
            <Subtitle2>${extraCosts.priceSeatRemaining}</Subtitle2>
          </div>
        </Banner>
      }
      <Container className="addMemberModalContainer" display='flex' flexDirection='column' gap='8px' width='100%'>
        <div className="flex gap-4">
          <TextField
            type="text"
            required
            value={name}
            variant="outlined"
            style={{ width: '50%' }}
            placeholder="First name"
            className={classes.input}
            onChange={(e) => {
              const REG_EXP: RegExp = /^[ A-Za-z]*$/
              if (REG_EXP.test(e.target.value)) setName(e.target.value)
            }}
          />
          <TextField
            type="text"
            required
            value={lastName}
            variant="outlined"
            style={{ width: '50%' }}
            placeholder="Last name"
            className={classes.input}
            onChange={(e) => {
              const REG_EXP: RegExp = /^[ A-Za-z]*$/
              if (REG_EXP.test(e.target.value)) setLastName(e.target.value)
            }}
          />
        </div>
        <TextField
          type="email"
          error={Boolean(email?.length && !isValidEmail(email))}
          required
          value={email}
          inputMode="email"
          variant="outlined"
          fullWidth placeholder="Email"
          className={classes.input}
          onChange={(e) => setEmail(e.target.value)}
        />
        <MemberPermissionsSelect
          value={role || ''}
          onChange={setRole}
          required
        />
        {role === OrganizationRole.ADMINISTRATOR &&
          <Banner className="addMemberModalBanner1" color={Colors.purpleLight} style={{ marginBottom: 16 }}>
            <Body2 color={Colors.textColor}>As a Team Admin, this teammate will have full access to update profiles, invite new teammates, create campaigns and websites, and update Billing settings.</Body2>
          </Banner>
        }
      </Container>

      {role === OrganizationRole.MEMBER &&
        <BrandsList brands={brandsLocal} onUpdateBrands={setBrandsLocal} />
      }

      {teamMembers.length + 1 > TEAM_PLAN_INCLUDED_SEATS &&
        <Banner
          className="addMemberModalBanner2"
          style={{ marginTop: 16 }}
          title="You've used 3 out of 3 included seats in your Team Plan."
          description={`Adding this member will add ${TEAM_PLAN_ADDITIONAL_SEAT_PRICE} / month to your subscription.`}
        />
      }
      <ButtonSymphony
        className="addMemberModalButtonTeam"
        style={{ marginTop: 16 }}
        isLoading={isLoading}
        onClick={onAddMember}
        disabled={isLoading || isMissingFields || brandsLocal.length === 0}
      >
        Send invite
      </ButtonSymphony>
    </ModalSymphony>
  );
};

export default AddMemberModal;
